/* eslint-disable max-lines */
import React, { useCallback, useMemo } from 'react'
import unescape from 'lodash/unescape'
import {
  CheckCircleIcon, IcoMoon, PlusIcon, Text,
} from '@usap/uicomponents'
import { StarCount } from '@usap/widgets'
import { commaSeparetedValue, dateFormatter, timeFormatterLocal } from '@usap/core'
import IInstallationShop from '../models/IInstallationShop'
import {
  StyledAppointmentWrapper,
  StyledAvailability,
  StyledAvgPrice,
  StyledButtonsWrapper,
  StyledInstallButton,
  StyledRating,
  StyledShopAddress,
  StyledShopAppointment,
  StyledShopAvgPriceWrapper,
  StyledShopHeader,
  StyledShopInfoWrapper,
  StyledShopName,
  StyledShopPriceWrapper,
  StyledShopRatingLabel,
  StyledShopSection,
} from './InstallAtShopInfo.css'
import ShopWarrantyMessage from '../ShopWarrantyMessage/ShopWarrantyMessage'
import DifmLocation from '../constants/DifmLocation'
import { GTM } from '../../../helpers'
import { ButtonSize, ButtonVariant } from '../Button/Button.config'
import Button from '../Button/Button'
import getAddressFromShopInfo from '../utility/getAddressFromShopInfo'
import { getInstallAtShopSeeMoreCTAId } from '../utility/anchorUtils'
import useIsRepairPalPricingEnabled from '../store/hooks/useIsRepairPalPricingEnabled'
import { getAllowedPartForDifm3 } from '../utility/allowedPartsForDifm3'
import InstallAtShopPrice from './InstallAtShopPrice'
import useWmoEnabled from '../../../hooks/useWmoEnabled'
import InstallAtShopInfoCartWmo from './InstallAtShopInfoCartWmo'
import InstallAtShopInfoCheckoutWmo from './InstallAtShopInfoCheckoutWmo'

// eslint-disable-next-line import/exports-last
export interface IInstallAtShopInfo {
  installationShop: IInstallationShop
  showNationalAvgCost: boolean
  disableBoldAppointmentTime?: boolean
  ActionsRender?: React.ReactNode
  sku: string
  pageName: DifmLocation
  productId?: number
  hideActionButtons?: boolean
  withPostalCode?: boolean
  partName: string
  isFirstAvailability: boolean
  onAddInstallation?: () => void
  onRemoveInstallation?: () => void
  onChangeInstallation?: () => void
  onOpenVehicleDetails?: () => void
}

const iconColor = '#2E4787'
const { installAtShopWidgetSelected, installAtShopWidgetRemoved } = GTM

const InstallAtShopInfo: React.FC<IInstallAtShopInfo> = ({
  installationShop,
  showNationalAvgCost,
  disableBoldAppointmentTime,
  ActionsRender,
  sku,
  pageName,
  hideActionButtons,
  withPostalCode = false,
  partName,
  isFirstAvailability,
  onAddInstallation,
  onRemoveInstallation,
  onChangeInstallation,
  onOpenVehicleDetails,
}) => {
  const {
    shopInfo,
    installPrice,
    installPriceMin,
    installPriceMax,
    nationalAvgCost,
    installDateTimeUtc,
    isSelected,
  } = installationShop
  const shopName = useMemo(() => unescape(shopInfo?.name), [shopInfo?.name])
  const lowerCasePartName = partName.toLocaleLowerCase()
  const isRppEnabled = useIsRepairPalPricingEnabled(getAllowedPartForDifm3(partName))
  const isWmoEnabled = useWmoEnabled()
  // eslint-disable-next-line no-nested-ternary
  const priceType = isRppEnabled && installPriceMin && installPriceMax ? 'range' : installationShop.installPrice ? 'single' : null

  const handleToggleInstallation = useCallback(() => {
    if (isSelected) {
      if (onRemoveInstallation) { onRemoveInstallation() }
      return installAtShopWidgetRemoved(pageName, lowerCasePartName, sku)
    }
    if (onAddInstallation) { onAddInstallation() }
    return installAtShopWidgetSelected(pageName, lowerCasePartName, sku)
  }, [isSelected, lowerCasePartName, onAddInstallation, onRemoveInstallation, pageName, sku])

  const handleChangeInstallation = useCallback(() => {
    if (priceType === 'range' && onOpenVehicleDetails) {
      return onOpenVehicleDetails()
    }

    if (onChangeInstallation && !hideActionButtons) {
      return onChangeInstallation()
    }
    return null
  }, [hideActionButtons, onChangeInstallation, onOpenVehicleDetails, priceType])

  const selectIcon = useMemo(() => (
    isSelected
      ? <IcoMoon icon={CheckCircleIcon} color={iconColor} size="24px" viewBoxProp="1 1 32 32" />
      : <IcoMoon icon={PlusIcon} color={iconColor} size="24px" viewBoxProp="0 0 24 24" />),
  [isSelected])

  const selectButtonLabel = useMemo(() => (isSelected ? <Text size="14px">Installation Added</Text>
    : <Text size="14px">Add Installation</Text>),
  [isSelected])

  const appointmentDate = useMemo(() => (
    <>
      {timeFormatterLocal(installDateTimeUtc)}
      {' - '}
      {dateFormatter(installDateTimeUtc)}
    </>
  ), [installDateTimeUtc])

  const milesToShop = `${commaSeparetedValue(shopInfo?.distance, 1)} mi`
  const shopAddress = ` - ${getAddressFromShopInfo(shopInfo, withPostalCode)}`

  if (!installationShop) return null

  if (pageName === 'Cart' && isWmoEnabled) {
    return (
      <InstallAtShopInfoCartWmo
        handleToggleInstallation={handleToggleInstallation}
        handleChangeInstallation={handleChangeInstallation}
        isRppEnabled={isRppEnabled}
        installPrice={installPrice}
        installPriceMin={installPriceMin}
        installPriceMax={installPriceMax}
        shopName={shopName}
        rating={shopInfo?.rating}
        ratingCount={shopInfo?.ratingCount}
        shopAddress={shopAddress}
        milesToShop={milesToShop}
        isFirstAvailability={isFirstAvailability}
        appointmentDate={appointmentDate}
        isSelected={isSelected}
        sku={sku}
        selectButtonLabel={selectButtonLabel}
        ActionsRender={ActionsRender}
        hideActionButtons={hideActionButtons}
      />
    )
  }

  if (pageName === 'Checkout' && isWmoEnabled) {
    return (
      <InstallAtShopInfoCheckoutWmo
        handleToggleInstallation={handleToggleInstallation}
        handleChangeInstallation={handleChangeInstallation}
        isRppEnabled={isRppEnabled}
        installPrice={installPrice}
        installPriceMin={installPriceMin}
        installPriceMax={installPriceMax}
        shopName={shopName}
        rating={shopInfo?.rating}
        ratingCount={shopInfo?.ratingCount}
        shopAddress={shopAddress}
        milesToShop={milesToShop}
        isFirstAvailability={isFirstAvailability}
        appointmentDate={appointmentDate}
        isSelected={isSelected}
        sku={sku}
        hideActionButtons={hideActionButtons}
        ActionsRender={ActionsRender}
        selectButtonLabel={selectButtonLabel}
      />
    )
  }

  return (
    <StyledShopSection isCart={pageName === 'Cart'} id={`install-at-shop-info-${sku}`}>
      <StyledShopHeader onClick={handleChangeInstallation} hideActionButtons={hideActionButtons || false}>
        <StyledShopPriceWrapper>
          Install at Shop
          {' '}
          <InstallAtShopPrice
            isRppEnabled={isRppEnabled}
            installPrice={installPrice}
            installPriceMin={installPriceMin}
            installPriceMax={installPriceMax}
          />
        </StyledShopPriceWrapper>
        {showNationalAvgCost && (
        <StyledShopAvgPriceWrapper>
          National Average Cost
          {' '}
          <StyledAvgPrice>
            $
            {commaSeparetedValue(nationalAvgCost)}
          </StyledAvgPrice>
        </StyledShopAvgPriceWrapper>
        )}
      </StyledShopHeader>

      <StyledShopInfoWrapper pageName={pageName}>
        <StyledAppointmentWrapper onClick={handleChangeInstallation} hideActionButtons={hideActionButtons || false}>
          <StyledShopAppointment disableBoldAppointmentTime={disableBoldAppointmentTime}>
            {appointmentDate}
          </StyledShopAppointment>
          {isFirstAvailability && (
          <StyledAvailability>First Availability</StyledAvailability>
          )}
        </StyledAppointmentWrapper>

        <StyledShopName onClick={handleChangeInstallation} hideActionButtons={hideActionButtons || false}>{shopName}</StyledShopName>
        <StyledRating onClick={handleChangeInstallation} hideActionButtons={hideActionButtons || false}>
          <StarCount ratings={shopInfo?.rating} />
          <StyledShopRatingLabel>
            (
            {shopInfo?.ratingCount}
            )
          </StyledShopRatingLabel>
        </StyledRating>
        <StyledShopAddress hideActionButtons={hideActionButtons || false} onClick={handleChangeInstallation}>
          {milesToShop}
          {shopAddress}
        </StyledShopAddress>
      </StyledShopInfoWrapper>

      {!ActionsRender && !hideActionButtons && (
        <StyledButtonsWrapper>
          <StyledInstallButton
            id={`add-installation-cta-${pageName}-${sku}`}
            custom
            icon={selectIcon}
            primary={false}
            label={selectButtonLabel}
            onClick={handleToggleInstallation}
            isSelected={isSelected}
          />
          <Button
            id={getInstallAtShopSeeMoreCTAId(sku)}
            label="Change Appointment"
            size={ButtonSize.Small}
            variant={ButtonVariant.Ghost}
            onClick={handleChangeInstallation}
          />
        </StyledButtonsWrapper>
      )}

      <ShopWarrantyMessage short={['Checkout', 'ConfirmationPage'].includes(pageName)} />

      {Boolean(ActionsRender) && ActionsRender}
    </StyledShopSection>
  )
}

export default React.memo(InstallAtShopInfo)
